import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Save, Loader2, ArrowLeft, FileSignature, Download, PenTool, Check, FileWarning } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AttributionChips from "@/components/AttributionChips";
import { useAuth } from "@/lib/auth";
import SignaturePad from "@/components/SignaturePad";
import CountryCombobox from "@/components/profile/CountryCombobox";
import PickupReturnChecklist from "@/components/booking/PickupReturnChecklist";
import BookingDocumentsPanel from "@/components/booking/BookingDocumentsPanel";
import LateReturnPanel from "@/components/booking/LateReturnPanel";
import VehicleSwapDialog from "@/components/booking/VehicleSwapDialog";
import DirectionsLink from "@/components/booking/DirectionsLink";
import { BookingTimeline } from "@/components/customer/MobileDashboard";
import { API } from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
};
const todayString = () => new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

// Small helpers extracted from the JSX to keep nested ternaries out of the render tree.
function _dashboardHrefForRole(role) {
  if (role === "admin") return "/dashboard/admin";
  if (role === "vendor") return "/dashboard/vendor";
  return "/dashboard/customer";
}

function _depositMethodLabel(method) {
  if (method === "cash") return "Cash only";
  if (method === "card") return "Credit / debit card only";
  return "Cash or credit / debit card";
}

function _mileageValueNode(car) {
  if (car.mileage_policy === "unlimited") return "Unlimited";
  if (car.mileage_policy === "limited") {
    return (
      <>
        {Math.round(car.mileage_limit_km_per_day || 0)} km/day ·{" "}
        <Money amount={car.mileage_extra_km_rate || 0} />/extra km
      </>
    );
  }
  return "—";
}

// Stream the server-rendered PDF to the user. Credentials are required so the httpOnly
// `access_token` cookie travels with the request; we then offer the blob as a download.
async function downloadAgreementPdf(bookingId, lang = "en") {
  const suffix = lang === "sq" ? "-SQ" : "";
  return _downloadPdf(bookingId, "agreement", `rental-agreement-${bookingId.slice(0, 8).toUpperCase()}${suffix}.pdf`, `?lang=${lang}`);
}

// Iter 67ed — standalone damage-inspection report. Separate endpoint so
// ops can hand insurers a damage-only document without the rest of the
// agreement context.
async function downloadDamageReportPdf(bookingId) {
  return _downloadPdf(bookingId, "damage-report", `damage-report-${bookingId.slice(0, 8).toUpperCase()}.pdf`);
}

async function _downloadPdf(bookingId, kind, filename, query = "") {
  try {
    const res = await fetch(`${API}/bookings/${bookingId}/${kind}.pdf${query}`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  } catch (e) {
    toast.error("Could not generate PDF. Please try again.");
  }
}

const Field = ({ label, value, mono = false, testId }) => (
  <div className="break-inside-avoid" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{label}</div>
    <div className={`text-sm ${mono ? "font-mono" : ""} text-neutral-900 mt-0.5`}>{value || "—"}</div>
  </div>
);

// Edit form for the vendor at handover (or admin overriding). Customers cannot mutate the
// second-driver fields here per business rule; backend enforces. The form sits inside a
// collapsible "Edit details" panel so the on-screen agreement isn't crowded by inputs.
function EditAgreementPanel({ booking, onSaved }) {
  const cd = booking.customer_details || {};
  const sd = booking.second_driver || {};
  const hasSecondDriver = (booking.add_ons || []).some((a) => (a.name || "").toLowerCase().startsWith("second driver"));
  const [form, setForm] = useState({
    customer_details: {
      full_name: cd.full_name || "",
      address: cd.address || "",
      country: cd.country || "",
      email: cd.email || "",
      phone: cd.phone || cd.whatsapp || "",
      id_number: cd.id_number || "",
      license_number: cd.license_number || "",
      license_expiry: cd.license_expiry || "",
    },
    second_driver: {
      full_name: sd.full_name || "",
      license_number: sd.license_number || "",
      license_expiry: sd.license_expiry || "",
    },
  });
  const [saving, setSaving] = useState(false);

  const setCD = (k) => (e) => setForm((f) => ({ ...f, customer_details: { ...f.customer_details, [k]: e.target.value } }));
  const setCDValue = (k) => (v) => setForm((f) => ({ ...f, customer_details: { ...f.customer_details, [k]: v } }));
  const setSD = (k) => (e) => setForm((f) => ({ ...f, second_driver: { ...f.second_driver, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { customer_details: form.customer_details };
      if (hasSecondDriver) payload.second_driver = form.second_driver;
      await api.patch(`/bookings/${booking.id}/agreement`, payload);
      toast.success("Agreement details saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <details className="rounded-2xl border border-border bg-white p-5 print:hidden" data-testid="agreement-edit-panel">
      <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-neutral-500" />
        Edit details (vendor / admin / customer)
      </summary>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs">Full name</Label><Input className="rounded-lg mt-1" value={form.customer_details.full_name} onChange={setCD("full_name")} data-testid="agr-full_name" /></div>
        <div><Label className="text-xs">Email</Label><Input className="rounded-lg mt-1" type="email" value={form.customer_details.email} onChange={setCD("email")} data-testid="agr-email" /></div>
        <div><Label className="text-xs">Phone or WhatsApp</Label><Input className="rounded-lg mt-1" value={form.customer_details.phone} onChange={setCD("phone")} data-testid="agr-phone" /></div>
        <div><Label className="text-xs">Passport / ID number</Label><Input className="rounded-lg mt-1" value={form.customer_details.id_number} onChange={setCD("id_number")} data-testid="agr-id_number" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Full address</Label><Input className="rounded-lg mt-1" value={form.customer_details.address} onChange={setCD("address")} placeholder="Street, city, postcode" data-testid="agr-address" /></div>
        <div>
          <Label className="text-xs">Country</Label>
          <div className="mt-1">
            <CountryCombobox value={form.customer_details.country || ""} onChange={setCDValue("country")} placeholder="Country…" testId="agr-country" />
          </div>
        </div>
        <div><Label className="text-xs">Driver licence number</Label><Input className="rounded-lg mt-1" value={form.customer_details.license_number} onChange={setCD("license_number")} data-testid="agr-license_number" /></div>
        <div><Label className="text-xs">Licence expiry</Label><Input className="rounded-lg mt-1" type="date" value={form.customer_details.license_expiry} onChange={setCD("license_expiry")} data-testid="agr-license_expiry" /></div>
      </div>
      {hasSecondDriver && (
        <>
          <div className="mt-4 mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">Second driver</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Full name</Label><Input className="rounded-lg mt-1" value={form.second_driver.full_name} onChange={setSD("full_name")} data-testid="agr-sd-full_name" /></div>
            <div><Label className="text-xs">Driver license #</Label><Input className="rounded-lg mt-1" value={form.second_driver.license_number} onChange={setSD("license_number")} data-testid="agr-sd-license_number" /></div>
            <div><Label className="text-xs">License expiry</Label><Input className="rounded-lg mt-1" type="date" value={form.second_driver.license_expiry} onChange={setSD("license_expiry")} data-testid="agr-sd-license_expiry" /></div>
          </div>
        </>
      )}
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full" data-testid="agr-save">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save
        </Button>
      </div>
    </details>
  );
}

export default function RentalAgreement() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/agreement`);
      setCtx(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load agreement");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bookingId]);

  // Iter 99 — customer returns from the Stripe balance Checkout: confirm and apply.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("balance") === "success") {
      api.post(`/bookings/${bookingId}/payment-link/check`).then((r) => {
        if (r.data.status === "paid") toast.success("Balance payment received — thank you!");
        window.history.replaceState({}, "", window.location.pathname);
        load();
      }).catch(() => {});
    }
    // Iter 100 — deposit pre-authorization return leg.
    if (params.get("deposit") === "success") {
      api.post(`/bookings/${bookingId}/payment-link/check?purpose=deposit`).then((r) => {
        if (r.data.status === "held") toast.success("Deposit hold authorized — thank you!");
        window.history.replaceState({}, "", window.location.pathname);
        load();
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [bookingId]);

  if (error) return <div className="max-w-3xl mx-auto p-12 text-center text-rose-600" data-testid="agreement-error">{error}</div>;
  if (!ctx) return <div className="max-w-3xl mx-auto p-12 text-center text-neutral-500">Loading agreement…</div>;

  const { booking, car, vendor, customer, second_driver: secondDriver, has_second_driver_addon: hasSD } = ctx;
  const totalDays = (() => {
    if (!booking.start_date || !booking.end_date) return null;
    return Math.max(1, Math.round((new Date(booking.end_date) - new Date(booking.start_date)) / 86400000));
  })();

  return (
    <div className="bg-neutral-50 min-h-screen print:bg-white">
      {/* Toolbar (hidden when printing) */}
      <div className="bg-white border-b border-border print:hidden">
        {/* Iter 67ff — mobile-first layout: stack the back-link above a horizontally
            scrollable action row so the four pill buttons stop overflowing the
            viewport. ≥ sm uses the original side-by-side layout. */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <Link
            to={_dashboardHrefForRole(user?.role)}
            className="inline-flex items-center gap-1.5 text-sm text-neutral-700 hover:text-neutral-900 flex-shrink-0"
            data-testid="agr-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
          <div
            className="flex items-center gap-2 overflow-x-auto sm:overflow-visible -mx-4 sm:mx-0 px-4 sm:px-0 pb-1 sm:pb-0 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none" }}
          >
            <Button variant="outline" size="sm" onClick={() => window.print()} className="rounded-full flex-shrink-0 sm:size-default" data-testid="agr-print">
              <Printer className="h-4 w-4 mr-1.5" /> Print
            </Button>
            <Button size="sm" onClick={() => downloadAgreementPdf(booking.id)} className="rounded-full flex-shrink-0 sm:size-default" data-testid="agr-download-pdf">
              <Download className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadAgreementPdf(booking.id, "sq")} className="rounded-full flex-shrink-0 sm:size-default" data-testid="agr-download-pdf-sq">
              <Download className="h-4 w-4 mr-1.5" /> PDF Shqip
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadDamageReportPdf(booking.id)}
              className="rounded-full flex-shrink-0 sm:size-default"
              data-testid="agr-download-damage-report"
              title="Inspection-only PDF with pickup baseline + return diff"
            >
              <FileWarning className="h-4 w-4 mr-1.5" /> Damage report
            </Button>
            {/* Iter 67ei — Swap vehicle. Only visible to vendor/admin/agent;
                the dialog's permission gate is enforced server-side. */}
            {(user?.role === "vendor" || user?.role === "admin" || user?.role === "agent") && (
              <VehicleSwapDialog
                booking={booking}
                onSwapped={load}
                buttonLabel="Swap vehicle"
                buttonClassName="rounded-full flex-shrink-0"
                buttonSize="sm"
              />
            )}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-8 print:p-0 print:max-w-full">
        {/* Iter 67bt — inline attribution chips for the agreement record. Hidden
            when printing because the formal document has its own signed-by /
            signed-at section per party. */}
        <div className="mb-3 print:hidden">
          <AttributionChips
            entity={booking}
            actions={["create", "update", "sign", "cancel"]}
            className=""
          />
        </div>
        {/* Iter 93 — reservation progress timeline (details page). */}
        <div className="print:hidden"><BookingTimeline booking={booking} /></div>
        {/* Iter 99 — pay-remaining-balance banner (Stripe link created by the vendor). */}
        {booking.balance_payment?.url && booking.balance_payment?.status !== "paid" && (
          <div className="print:hidden mb-4 rounded-2xl border-2 border-blue-300 bg-blue-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pay-balance-banner">
            <div className="flex-1">
              <div className="font-bold text-blue-900">Remaining balance: <Money amount={booking.balance_payment.amount || 0} /></div>
              <div className="text-sm text-blue-800/80">Pay securely online by card — no need to bring cash to pickup.</div>
            </div>
            <a
              href={booking.balance_payment.url}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white text-sm font-bold px-5 py-3 hover:bg-blue-700 transition"
              data-testid="pay-balance-btn"
            >
              Pay remaining balance
            </a>
          </div>
        )}
        {booking.balance_payment?.status === "paid" && (
          <div className="print:hidden mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-700" data-testid="balance-paid-banner">
            ✓ Remaining balance of <Money amount={booking.balance_payment.amount || 0} /> paid online
          </div>
        )}
        {/* Iter 100 — deposit pre-authorization banner for the customer. */}
        {booking.deposit_hold?.url && booking.deposit_hold?.status !== "held" && (
          <div className="print:hidden mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="deposit-hold-banner">
            <div className="flex-1">
              <div className="font-bold text-amber-900">Security deposit: <Money amount={booking.deposit_hold.amount || 0} /></div>
              <div className="text-sm text-amber-800/80">Pre-authorize your card — funds are held, not charged.</div>
            </div>
            <a href={booking.deposit_hold.url} className="inline-flex items-center justify-center rounded-xl bg-amber-600 text-white text-sm font-bold px-5 py-3 hover:bg-amber-700 transition" data-testid="deposit-hold-btn">
              Authorize deposit
            </a>
          </div>
        )}
        {booking.deposit_hold?.status === "held" && (
          <div className="print:hidden mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-700" data-testid="deposit-held-banner">
            ✓ Security deposit of <Money amount={booking.deposit_hold.amount || 0} /> authorized (card hold)
          </div>
        )}
        <EditAgreementPanel booking={booking} onSaved={load} />
        <LateReturnPanel booking={booking} role={user?.role} onApplied={load} />

        {/* The agreement itself */}
        <article className="bg-white rounded-2xl border border-border mt-6 p-10 print:rounded-none print:border-0 print:shadow-none print:p-12 print:mt-0" data-testid="rental-agreement-document">
          {/* Header */}
          <header className="border-b border-neutral-200 pb-6 mb-6 flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Rental Agreement</div>
              <h1 className="font-display text-3xl font-medium mt-1">Vehicle Rental Contract</h1>
              <div className="text-xs text-neutral-500 mt-1">Issued {todayString()} · Booking #{booking.id?.slice(0, 8).toUpperCase()}</div>
            </div>
            <div className="text-right flex items-start gap-3 justify-end">
              {vendor.business_logo && (
                <img
                  src={vendor.business_logo.startsWith("http") ? vendor.business_logo : fileUrl(vendor.business_logo)}
                  alt={vendor.business_name || "Vendor logo"}
                  className="h-14 w-14 rounded-xl object-contain border border-border bg-white shrink-0"
                  data-testid="agreement-vendor-logo"
                />
              )}
              <div>
                <div className="font-display text-lg font-semibold">{vendor.business_name || vendor.name}</div>
                {vendor.responsible_person && <div className="text-xs text-neutral-600 mt-0.5">Represented by {vendor.responsible_person}</div>}
                {vendor.business_address && <div className="text-xs text-neutral-600 max-w-[260px] mt-1">{vendor.business_address}</div>}
                {vendor.vat_number && <div className="text-xs text-neutral-500 mt-1">VAT/NIPT: <span className="font-mono">{vendor.vat_number}</span></div>}
                {vendor.business_email && <div className="text-xs text-neutral-500 mt-0.5">{vendor.business_email}</div>}
              </div>
            </div>
          </header>

          {/* Parties */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div data-testid="agreement-vendor-block">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Provider</div>
              <div className="space-y-3">
                <Field label="Business name" value={vendor.business_name || vendor.name} />
                {vendor.responsible_person && <Field label="Responsible person" value={vendor.responsible_person} />}
                <Field label="Address" value={vendor.business_address} />
                <Field label="VAT / NIPT" value={vendor.vat_number} mono />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" value={vendor.phone} />
                  <Field label="WhatsApp" value={vendor.whatsapp} />
                </div>
                <Field label="Email" value={vendor.business_email || vendor.email} />
              </div>
            </div>
            <div data-testid="agreement-customer-block">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Renter / Primary driver</div>
              <div className="space-y-3">
                <Field label="Full name" value={customer.full_name} />
                <Field label="Email" value={customer.email} />
                <Field label="Phone / WhatsApp" value={customer.phone || customer.whatsapp} />
                <Field label="Passport / ID number" value={customer.id_number} mono />
                <Field label="Full address" value={customer.address} />
                <Field label="Country" value={customer.country} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Driver licence #" value={customer.license_number} mono />
                  <Field label="Expiry" value={formatDate(customer.license_expiry)} />
                </div>
              </div>
            </div>
          </section>

          {/* Iter 67eg — Customer KYC documents (driver licence / ID / passport).
              Renters see their own original uploads; vendors / admins / agents
              see watermarked PNG renders with the viewer's email + UTC
              timestamp baked in. Hidden when printing — the formal PDF has
              its own dedicated identity verification block. */}
          {customer?.id && (customer.documents || []).length > 0 && (
            <section className="mb-8 print:hidden" data-testid="agreement-customer-docs">
              <CustomerIdentityDocsViewer
                customerId={customer.id}
                documents={customer.documents || []}
                viewerIsSelf={user?.id === customer.id}
              />
            </section>
          )}

          {/* Second driver */}
          {hasSD && (
            <section className="mb-8 rounded-xl border border-neutral-200 p-5 bg-neutral-50/50 break-inside-avoid" data-testid="agreement-second-driver-block">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Second driver</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Full name" value={secondDriver?.full_name} />
                <Field label="Driver license #" value={secondDriver?.license_number} mono />
                <Field label="Expiry" value={formatDate(secondDriver?.license_expiry)} />
              </div>
            </section>
          )}

          {/* Vehicle + booking details */}
          <section className="mb-8" data-testid="agreement-vehicle-block">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Vehicle</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Make / Model" value={`${car.make || ""} ${car.model || ""}`.trim()} />
              <Field label="Year" value={car.year} />
              <Field label="Category" value={car.car_type} />
              <Field
                label="Body type"
                value={(ctx.agreement_body_type || car.car_type || "").trim() || "—"}
                testId="agreement-body-type-value"
              />
              <Field label="Engine" value={car.engine_size} />
              <Field label="Transmission" value={car.transmission} />
              <Field label="Fuel" value={car.fuel_type} />
              <Field label="Seats" value={car.seats} />
              <Field label="Doors" value={car.doors} />
            </div>
            <p className="mt-2 text-[11px] text-neutral-500">
              Body type is used in pickup &amp; drop-off inspection to identify the vehicle when recording accidents, scratches, dents or damages.
            </p>
          </section>

          <section className="mb-8 rounded-xl bg-blue-50/40 border border-blue-100 p-5" data-testid="agreement-booking-block">
            <div className="text-xs uppercase tracking-widest text-blue-900/60 font-semibold mb-3">Rental terms</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Pick-up" value={`${formatDate(booking.start_date)}${booking.pickup_time ? ` · ${booking.pickup_time}` : ""}`} />
              <Field label="Drop-off" value={`${formatDate(booking.end_date)}${booking.return_time ? ` · ${booking.return_time}` : ""}`} />
              <Field
                label="Pick-up location"
                value={
                  booking.pickup_location ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{booking.pickup_location}</span>
                      <DirectionsLink location={booking.pickup_location} testId="agreement-pickup-directions" />
                    </span>
                  ) : "—"
                }
              />
              <Field
                label="Drop-off location"
                value={
                  booking.dropoff_location ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{booking.dropoff_location}</span>
                      <DirectionsLink location={booking.dropoff_location} testId="agreement-dropoff-directions" />
                    </span>
                  ) : "—"
                }
              />
              <Field label="Days" value={totalDays} />
              <Field label="Daily rate" value={(booking.rental_subtotal && totalDays) ? <Money amount={booking.rental_subtotal / totalDays} /> : "—"} />
              <Field label="Insurance fee" value={<Money amount={booking.insurance_fee || 0} />} />
              <Field label="Add-ons" value={<Money amount={booking.add_ons_fee || 0} />} />
              <Field label="Route fee" value={<Money amount={booking.route_fee || 0} />} />
              <Field label="Deposit" value={<Money amount={booking.deposit || 0} />} />
              {car.deposit_payment_method && Number(booking.deposit || 0) > 0 && (
                <Field
                  label="Deposit payment"
                  testId="agreement-deposit-method"
                  value={_depositMethodLabel(car.deposit_payment_method)}
                />
              )}
              <Field label="Fuel policy" value={(car.fuel_policy || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} />
              <Field label="Mileage" value={_mileageValueNode(car)} />
              <Field label="Total amount" value={<span className="font-semibold"><Money amount={booking.amount || 0} /></span>} />
            </div>
          </section>

          {/* Add-ons list */}
          {(booking.add_ons || []).length > 0 && (
            <section className="mb-8" data-testid="agreement-addons-block">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Selected add-ons</div>
              <ul className="space-y-1">
                {booking.add_ons.map((a, i) => (
                  <li key={a.id || a.name || `addon-${i}`} className="flex items-center justify-between text-sm border-b border-neutral-100 py-1.5">
                    <span>{a.name}{(a.qty || 1) > 1 ? ` ×${a.qty}` : ""}</span>
                    <span className="font-medium"><Money amount={a.fee || 0} /></span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(booking.custom_charges || []).length > 0 && (
            <section className="mb-8" data-testid="agreement-custom-charges-block">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-3">Other charges</div>
              <ul className="space-y-1">
                {booking.custom_charges.map((c, i) => (
                  <li key={`cc-${i}`} className="flex items-center justify-between text-sm border-b border-neutral-100 py-1.5">
                    <span>{c.label}</span>
                    <span className="font-medium"><Money amount={c.amount || 0} /></span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Signatures */}
          <SignaturesSection ctx={ctx} onSigned={load} />

          {/* Pickup / Return inspection checklist with per-phase signatures */}          <PickupReturnChecklist booking={booking} />

          {/* Photos & documents (pickup / return evidence) */}
          <div className="mt-8">
            <BookingDocumentsPanel bookingId={booking.id} currentUserId={user?.id} />
          </div>
        </article>
      </main>

      {/* Print stylesheet — A4, 11pt minimum body text when printed (Iter 112) */}
      <style>{`
        @media print {
          @page { margin: 12mm; size: A4; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; font-size: 11pt; }
          article { font-size: 11pt; }
          article p, article div, article span, article li, article td, article th, article dt, article dd { font-size: 11pt !important; line-height: 1.35 !important; }
          article h1 { font-size: 15pt !important; }
          article h2, article h3 { font-size: 12pt !important; font-weight: 700 !important; }
        }
      `}</style>
    </div>
  );
}


/**
 * Per-booking signing UI. Shows BOTH parties' captured signatures (rendered for print/PDF),
 * and — for the party that hasn't signed yet AND is the current user — exposes an inline
 * pad to draw + save a fresh signature. The vendor's permanent profile signature is offered
 * as a 1-click apply so they don't have to redraw on every booking. Customer can do the
 * same if they previously saved a profile signature.
 *
 * Render rules:
 *   • Always show both signature slots with the captured image when present.
 *   • Hide the "Sign here" pad in print/PDF — only the signed image should appear.
 *   • Customer can only sign as `customer`; vendor (or admin) only as `vendor`.
 */
function SignaturesSection({ ctx, onSigned }) {
  const { user } = useAuth();
  const booking = ctx.booking || {};
  const vendor = ctx.vendor || {};
  const customer = ctx.customer || {};
  const sigs = ctx.signatures || {};

  const isCustomer = user?.id === booking.customer_id;
  const isVendor = user?.id === booking.vendor_id && user?.role === "vendor";
  const isAdmin = user?.role === "admin";

  return (
    <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-12 break-inside-avoid" data-testid="agreement-signatures">
      <SignatureSlot
        title="Provider signature"
        label={vendor.business_name || vendor.name}
        image={sigs.vendor_image}
        signedAt={sigs.vendor_signed_at}
        canSign={isVendor || isAdmin}
        party="vendor"
        bookingId={booking.id}
        profileImage={user?.signature_image}
        onSigned={onSigned}
        testIdPrefix="vendor"
      />
      <SignatureSlot
        title="Renter signature"
        label={customer.full_name || "—"}
        image={sigs.customer_image}
        signedAt={sigs.customer_signed_at}
        canSign={isCustomer}
        party="customer"
        bookingId={booking.id}
        profileImage={user?.signature_image}
        onSigned={onSigned}
        testIdPrefix="customer"
      />
    </section>
  );
}

function SignatureSlot({ title, label, image, signedAt, canSign, party, bookingId, profileImage, onSigned, testIdPrefix }) {
  const [drawing, setDrawing] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (img) => {
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/sign`, { image: img, party });
      toast.success("Signature applied");
      setOpen(false);
      setDrawing("");
      onSigned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply signature");
    } finally {
      setSaving(false);
    }
  };

  const formatSignedAt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div data-testid={`${testIdPrefix}-signature-slot`}>
      <div className="border-t border-neutral-300 pt-2 mt-12 min-h-[120px]">
        <div className="text-xs text-neutral-500">{title}</div>
        {image ? (
          <div className="mt-1">
            <img src={image} alt={`${title} signature`} className="max-h-20 max-w-full object-contain" data-testid={`${testIdPrefix}-signature-image`} />
            <div className="text-sm font-medium mt-1">{label}</div>
            {signedAt && (
              <div className="text-[10px] text-neutral-500 mt-0.5 inline-flex items-center gap-1" data-testid={`${testIdPrefix}-signed-at`}>
                <Check className="h-3 w-3" /> Signed {formatSignedAt(signedAt)}
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm font-medium mt-12">{label}</div>
        )}
      </div>

      {canSign && (
        <div className="print:hidden mt-3">
          {!open ? (
            <Button
              type="button"
              variant={image ? "outline" : "default"}
              size="sm"
              onClick={() => setOpen(true)}
              className="rounded-full"
              data-testid={`${testIdPrefix}-sign-toggle`}
            >
              <PenTool className="h-3.5 w-3.5 mr-1.5" />
              {image ? "Re-sign agreement" : "Sign here"}
            </Button>
          ) : (
            <div className="mt-2 rounded-2xl border border-border bg-neutral-50 p-4 space-y-3" data-testid={`${testIdPrefix}-sign-panel`}>
              <SignaturePad
                value={drawing}
                onChange={setDrawing}
                width={420}
                height={150}
                testId={`${testIdPrefix}-sign-pad`}
              />
              <div className="flex items-center justify-between flex-wrap gap-2">
                {profileImage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => submit(profileImage)}
                    disabled={saving}
                    className="rounded-full text-xs"
                    data-testid={`${testIdPrefix}-use-saved-signature`}
                  >
                    Use my saved signature
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setOpen(false); setDrawing(""); }}
                    disabled={saving}
                    className="rounded-full"
                    data-testid={`${testIdPrefix}-sign-cancel`}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => submit(drawing)}
                    disabled={saving || !drawing}
                    className="rounded-full"
                    data-testid={`${testIdPrefix}-sign-save`}
                  >
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                    Apply signature
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Iter 67eg — Inline KYC documents viewer. Vendors / admins / agents render
// each image via `/api/users/{customer_id}/documents/{doc_id}/watermarked`
// which overlays a diagonal repeating RentalCar.al filigrane with the
// viewer's email + UTC timestamp — any screenshot is forensically
// traceable back to the session that produced it. The renter themselves
// fetches the original (no watermark) because they need to verify what
// they uploaded.
const _DOC_LABEL = {
  driver_license: "Driver's licence (front)",
  driver_license_back: "Driver's licence (back)",
  id_card: "ID card / passport",
  passport: "Passport",
  national_id: "National ID",
};

function CustomerIdentityDocsViewer({ customerId, documents, viewerIsSelf }) {
  const [open, setOpen] = useState(null); // {id, url} for the modal preview
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="customer-id-docs-viewer">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
            Identity verification · {viewerIsSelf ? "your originals" : "watermarked copies"}
          </div>
          <div className="text-xs text-neutral-600 mt-0.5 max-w-xl">
            {viewerIsSelf
              ? "These are the original files you uploaded to your profile. Vendors see watermarked copies only."
              : "Each render carries a diagonal RentalCar.al filigrane with your email + a UTC timestamp. Originals are kept untouched for legal disputes."}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {documents.map((d) => {
          const url = `${API}/users/${customerId}/documents/${d.id}/watermarked`;
          const isImage = (d.content_type || "").startsWith("image/");
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => setOpen({ id: d.id, url, label: _DOC_LABEL[d.kind] || d.kind, isImage })}
              className="text-left rounded-xl border border-border bg-white overflow-hidden hover:shadow-md transition group"
              data-testid={`customer-doc-${d.kind}`}
            >
              <div className="aspect-[3/2] bg-neutral-100 flex items-center justify-center overflow-hidden">
                {isImage ? (
                  <img
                    src={url}
                    alt={_DOC_LABEL[d.kind] || d.kind}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition"
                  />
                ) : (
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                    PDF · {d.filename || "document"}
                  </div>
                )}
              </div>
              <div className="px-3 py-2">
                <div className="text-xs font-semibold truncate">{_DOC_LABEL[d.kind] || d.kind}</div>
                <div className="text-[10px] text-neutral-500 mt-0.5">
                  {viewerIsSelf ? "Original" : "Watermarked"}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {open && (
        <div
          role="dialog"
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="customer-doc-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(null)}
              className="absolute -top-10 right-0 text-white text-sm hover:underline"
              data-testid="customer-doc-modal-close"
            >
              Close ×
            </button>
            {open.isImage ? (
              <img src={open.url} alt={open.label} className="w-full max-h-[85vh] object-contain rounded-xl" />
            ) : (
              <iframe src={open.url} title={open.label} className="w-full h-[85vh] rounded-xl bg-white" />
            )}
            <div className="mt-2 text-white text-xs opacity-70 text-center">
              {open.label} · {viewerIsSelf ? "Original" : "Watermarked rendering — do not screenshot"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
